import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  EyeOutlined, 
  ScanOutlined, 
  SafetyOutlined,
  CameraOutlined,
  RobotOutlined,
  DashboardOutlined,
  ArrowRightOutlined
} from '@ant-design/icons'

const Services = () => {
  const services = [
    {
      icon: <EyeOutlined />,
      title: '目标检测与识别',
      description: '实时检测和识别图像或视频中的多个目标对象',
      features: [
        '多目标实时检测',
        '高精度目标识别',
        '目标追踪与计数',
        '异常行为检测',
      ],
      applications: ['智能安防', '交通监控', '零售分析'],
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: <ScanOutlined />,
      title: '图像分割',
      description: '像素级的图像理解，精准分割目标区域',
      features: [
        '语义分割',
        '实例分割',
        '全景分割',
        '医学影像分割',
      ],
      applications: ['医疗诊断', '自动驾驶', '遥感分析'],
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: <SafetyOutlined />,
      title: '缺陷检测',
      description: '工业级质量检测，自动识别产品缺陷',
      features: [
        '表面缺陷检测',
        '尺寸测量',
        '质量分级',
        '实时报警',
      ],
      applications: ['智能制造', '质量控制', '生产监控'],
      color: 'from-green-500 to-teal-500',
    },
    {
      icon: <CameraOutlined />,
      title: 'OCR识别',
      description: '多语言文字识别，支持各类场景',
      features: [
        '印刷体识别',
        '手写体识别',
        '多语言支持',
        '版面分析',
      ],
      applications: ['文档数字化', '票据识别', '车牌识别'],
      color: 'from-orange-500 to-red-500',
    },
    {
      icon: <RobotOutlined />,
      title: '人脸识别',
      description: '高精度人脸检测、识别与分析',
      features: [
        '人脸检测定位',
        '1:1人脸比对',
        '1:N人脸搜索',
        '活体检测',
      ],
      applications: ['门禁考勤', '身份验证', '智能零售'],
      color: 'from-indigo-500 to-purple-500',
    },
    {
      icon: <DashboardOutlined />,
      title: '行为分析',
      description: '智能分析人员行为和事件',
      features: [
        '动作识别',
        '姿态估计',
        '人群分析',
        '异常检测',
      ],
      applications: ['智慧城市', '安全监控', '运动分析'],
      color: 'from-cyan-500 to-blue-500',
    },
  ]

  const process = [
    {
      step: '01',
      title: '需求分析',
      description: '深入了解客户业务场景和技术需求',
    },
    {
      step: '02',
      title: '方案设计',
      description: '制定定制化的AI视觉解决方案',
    },
    {
      step: '03',
      title: '数据准备',
      description: '数据采集、清洗、标注和增强',
    },
    {
      step: '04',
      title: '模型训练',
      description: '使用深度学习算法训练优化模型',
    },
    {
      step: '05',
      title: '系统集成',
      description: '将AI能力集成到客户业务系统',
    },
    {
      step: '06',
      title: '运维支持',
      description: '持续优化和技术支持服务',
    },
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 页面头部 */}
      <section className="tech-bg py-20 pt-32">
        <div className="container-custom text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            技术服务
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            全方位的AI视觉技术服务，助力企业数字化转型
          </motion.p>
        </div>
      </section>

      {/* 技术服务 */}
      <section className="py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">核心服务</h2>
            <p className="section-subtitle">
              基于深度学习的AI视觉技术，覆盖多个应用场景
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card p-8 group hover:shadow-2xl transition-all"
              >
                <div className={`w-16 h-16 rounded-lg bg-gradient-to-br ${service.color} flex items-center justify-center text-white text-3xl mb-6`}>
                  {service.icon}
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {service.description}
                </p>

                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">核心功能</h4>
                  <ul className="space-y-2">
                    {service.features.map(feature => (
                      <li key={feature} className="flex items-start gap-2 text-sm text-gray-600">
                        <ArrowRightOutlined className="text-primary-500 mt-1" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">应用场景</h4>
                  <div className="flex flex-wrap gap-2">
                    {service.applications.map(app => (
                      <span
                        key={app}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                      >
                        {app}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 技术流程 */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">服务流程</h2>
            <p className="section-subtitle">
              标准化的服务流程，确保项目高质量交付
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="card p-8">
                  <div className="text-6xl font-bold text-primary-100 mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600">
                    {item.description}
                  </p>
                </div>

                {/* 连接线 */}
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2">
                    <ArrowRightOutlined className="text-2xl text-primary-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-800">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              开始您的AI视觉项目
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              联系我们的技术专家，获取定制化的AI视觉解决方案
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-white text-primary-600 font-bold rounded-lg shadow-xl hover:shadow-2xl transition-all"
                >
                  免费咨询
                </motion.button>
              </Link>
              <Link to="/cases">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-primary-600 transition-all"
                >
                  查看案例
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Services

